<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { useDisplay } from 'vuetify'
import { DefalultOrder } from '@/services/types'
import OrderListbox from './OrderListbox.vue'
import Button from '@/components/Button.vue'

const props = defineProps<{
  order: string | null
  orderBy: string | null
  multiple?: boolean
  orderingOptions: { value: string; label: string }[]
  defaultOrder: DefalultOrder
}>()

const emit = defineEmits<{
  (e: 'order-by', value: string | null): void
  (e: 'order', value: string): void
  (e: 'clear-ordering'): void
  (e: 'close'): void
}>()

const isMenuOpen = ref(false)

const { smAndDown } = useDisplay()
const isMobile = computed(() => smAndDown.value)

// Ajustes responsivos do card/menu
const menuWidth = computed(() =>
  isMobile.value ? 'calc(100vw - 24px)' : '400px'
)
const menuMaxHeight = computed(() =>
  isMobile.value ? 'calc(100vh - 160px)' : 'unset'
)

/**
 * v-model dos listbox trabalha com LABEL
 * então aqui a gente guarda label, e converte pra value na hora de emitir
 */

// direção (asc/desc) – principal e secundário
const localOrder = ref<string>(props.order ?? props.defaultOrder.ORDER ?? '')
const secondaryLocalOrder = ref<string>(
  props.order ?? props.defaultOrder.ORDER ?? ''
)

// campo (label) – principal e secundário
const localOrderBy = ref<string | null>(null)
const secondaryLocalOrderBy = ref<string | null>(null)

function getLabelByValue(value: string | null): string | null {
  if (!value) return null
  const item = props.orderingOptions.find((entry) => entry.value === value)
  return item ? item.label : null
}

function getValueByLabel(label: string | null): string | null {
  if (!label) return null
  const item = props.orderingOptions.find((entry) => entry.label === label)
  return item ? item.value : null
}

watch(
  () => props.order,
  (val) => {
    const fallback = props.defaultOrder.ORDER ?? ''
    localOrder.value = val ?? fallback
    secondaryLocalOrder.value = val ?? fallback
  },
  { immediate: true }
)

watch(
  () => props.orderBy,
  (val) => {
    const baseOrderBy = val ?? props.defaultOrder.ORDERBY ?? null
    localOrderBy.value = getLabelByValue(baseOrderBy)
    secondaryLocalOrderBy.value = null
  },
  { immediate: true }
)

function handleOrderAndCloseMenu() {
  const primaryField = getValueByLabel(localOrderBy.value)
  const secondaryField = getValueByLabel(secondaryLocalOrderBy.value)

  const fields: string[] = []
  const directions: string[] = []

  if (primaryField && localOrder.value) {
    fields.push(primaryField)
    directions.push(localOrder.value)
  }

  if (props.multiple && secondaryField && secondaryLocalOrder.value) {
    fields.push(secondaryField)
    directions.push(secondaryLocalOrder.value)
  }

  if (!fields.length) {
    emit('clear-ordering')
    emit('order-by', null)
    emit('order', '')
    isMenuOpen.value = false
    emit('close')
    return
  }

  emit('order-by', fields.join(','))
  emit('order', directions.join(','))
  isMenuOpen.value = false
  emit('close')
}

function updateLocalOrder(val: string) {
  localOrder.value = val
}

function updateSecondaryLocalOrder(val: string) {
  secondaryLocalOrder.value = val
}

function handleClearOrdering() {
  localOrder.value = ''
  secondaryLocalOrder.value = ''
  localOrderBy.value = null
  secondaryLocalOrderBy.value = null

  emit('clear-ordering')
  emit('order-by', null)
  emit('order', '')
  isMenuOpen.value = false
  emit('close')
}
</script>

<template>
  <v-menu
    v-model="isMenuOpen"
    offset-y
    :close-on-content-click="false"
    :location="isMobile ? 'bottom' : 'bottom'"
    :origin="isMobile ? 'top center' : 'top end'"
    :location-strategy="isMobile ? 'connected' : 'connected'"
    :content-class="['order-menu', isMobile ? 'order-menu--mobile' : '']"
  >
    <template #activator="{ props: menuProps }">
      <v-btn
        id="order-menu"
        v-bind="menuProps"
        icon
        variant="plain"
        elevation="0"
        density="comfortable"
        :disabled="!orderingOptions?.length"
        :style="{ opacity: !orderingOptions?.length ? 0.3 : 1 }"
      >
        <v-icon id="order-button" size="24" class="ll-icon">
          mdi-swap-vertical
        </v-icon>
      </v-btn>
    </template>

    <v-card
      class="pa-4 order-card"
      :style="{
        width: menuWidth,
        maxHeight: menuMaxHeight,
        overflowY: isMobile ? 'auto' : 'visible',
      }"
    >
      <h3 class="text-subtitle-1 font-weight-bold mb-4">
        {{ $t('corporate.order.modalLabel') }}
      </h3>

      <OrderListbox
        v-model="localOrderBy"
        :order="localOrder"
        :ordering-options="orderingOptions"
        @update-order="updateLocalOrder"
      />

      <OrderListbox
        v-if="multiple"
        v-model="secondaryLocalOrderBy"
        :order="secondaryLocalOrder"
        :ordering-options="orderingOptions"
        is-secondary
        class="mt-4"
        @update-order="updateSecondaryLocalOrder"
      />

      <v-row class="mt-4" :justify="multiple ? 'end' : 'center'" dense>
        <v-col cols="auto" class="order-actions">
          <Button class="primaryOutline" @click="handleClearOrdering">
            {{ $t('corporate.order.clear') }}
          </Button>
        </v-col>
        <v-col cols="auto" class="order-actions">
          <Button
            id="save-order-button"
            class="primaryFlat"
            @click="handleOrderAndCloseMenu"
          >
            {{ $t('corporate.order.order') }}
          </Button>
        </v-col>
      </v-row>
    </v-card>
  </v-menu>
</template>

<style scoped>
.ll-icon {
  width: 24px;
  height: 24px;
  cursor: pointer;
  opacity: 0.8;
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.ll-icon:hover {
  background-color: #f1f8e9 !important;
  color: #558b2f !important;
  border-color: #f1f8e9 !important;
  font-weight: bold;
  font-size: 12px;
  box-shadow: none !important;
  opacity: 1;
  transform: scale(1.05);
}
.ll-icon--muted {
  opacity: 0.5;
}

.order-card {
  border-radius: 12px;
}

@media (max-width: 600px) {
  .order-actions :deep(button) {
    min-height: 40px;
  }
}
</style>
